import { useEffect, useState, useRef } from "react";
import { useFiltersStore } from "@/common/zustand/filters.store.js";
import { useDropdownsStore } from "@/common/zustand/dropdowns.store.js";
import { formatImporteToAmericano } from "@/common/utils/functions/formatCurrencyForApi.js";
import { fetchModalFilterData, fetchSectoresDropdown } from "../services/modalFilter.services.js";
import { useComprasAuth } from "@/common/hooks/useComprasAuth.js";

export const useModalFilter = () => {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // Obtener datos del usuario para cargar sectores
  const { username, userDetails } = useComprasAuth();
  const centroCosto = userDetails?.CENTRO_COSTO || null;

  // ✅ Usar dropdowns del store global
  const comprobantes = useDropdownsStore((s) => s.comprobantes);
  const estadosFactura = useDropdownsStore((s) => s.estadosFactura);
  const sectores = useDropdownsStore((s) => s.sectores);
  
  // Flags de carga del store
  const comprobantesLoaded = useDropdownsStore((s) => s.comprobantesLoaded);
  const estadosLoaded = useDropdownsStore((s) => s.estadosLoaded);
  const sectoresLoaded = useDropdownsStore((s) => s.sectoresLoaded);
  const setComprobantes = useDropdownsStore((s) => s.setComprobantes);
  const setEstadosFactura = useDropdownsStore((s) => s.setEstadosFactura);
  const setSectores = useDropdownsStore((s) => s.setSectores);
  const setLoadingComprobantes = useDropdownsStore((s) => s.setLoadingComprobantes);
  const setLoadingEstados = useDropdownsStore((s) => s.setLoadingEstados);
  const setLoadingSectores = useDropdownsStore((s) => s.setLoadingSectores);
  const needsReloadSectores = useDropdownsStore((s) => s.needsReloadSectores);

  // Zustand store actions/state
  const setTipoFactura = useFiltersStore((s) => s.setTipoFactura);
  const setEstadoFactura = useFiltersStore((s) => s.setEstadoFactura);
  const setImporteDesdeStore = useFiltersStore((s) => s.setImporteDesde);
  const setImporteHastaStore = useFiltersStore((s) => s.setImporteHasta);
  const setRazonSocialStore = useFiltersStore((s) => s.setRazonSocial);
  const setFechaDesdeStore = useFiltersStore((s) => s.setFechaDesde);
  const setFechaHastaStore = useFiltersStore((s) => s.setFechaHasta);
  const setSector = useFiltersStore((s) => s.setSector);
  const resetFilters = useFiltersStore((s) => s.resetFilters);

  // Obtener los VALORES actuales del store (no las funciones setter)
  const tipoFacturaValue = useFiltersStore((s) => s.tipoFactura);
  const estadoFacturaValue = useFiltersStore((s) => s.estadoFactura);
  const fechaDesdeValue = useFiltersStore((s) => s.fechaDesde);
  const fechaHastaValue = useFiltersStore((s) => s.fechaHasta);
  const razonSocialValue = useFiltersStore((s) => s.razonSocial);
  const importeDesdeValue = useFiltersStore((s) => s.importeDesde);
  const importeHastaValue = useFiltersStore((s) => s.importeHasta);
  const sectorValue = useFiltersStore((s) => s.sector);

  // Estados locales del formulario
  const [tipoFacturaSelected, setTipoFacturaSelected] = useState(tipoFacturaValue?.desc || null);
  const [estadoFacturaSelected, setEstadoFacturaSelected] = useState(estadoFacturaValue?.desc || null);
  const [importeDesde, setImporteDesde] = useState(importeDesdeValue);
  const [importeHasta, setImporteHasta] = useState(importeHastaValue);
  const [razonSocial, setRazonSocial] = useState(razonSocialValue);
  const [fechaDesde, setFechaDesde] = useState(fechaDesdeValue);
  const [fechaHasta, setFechaHasta] = useState(fechaHastaValue);
  const [sectorSelected, setSectorSelected] = useState(
    sectorValue?.desc || null
  );

  const loadingRef = useRef(false);
  const loadingSectoresRef = useRef(false);

  useEffect(() => {
    if (!isFilterModalOpen || (comprobantesLoaded && estadosLoaded) || loadingRef.current) {
      return;
    }

    const loadDropdowns = async () => {
      loadingRef.current = true;
      if (!comprobantesLoaded) setLoadingComprobantes(true);
      if (!estadosLoaded) setLoadingEstados(true);

      try {
        const { comprobantes, estadosFactura } = await fetchModalFilterData();
        if (!comprobantesLoaded) setComprobantes(comprobantes);
        if (!estadosLoaded) setEstadosFactura(estadosFactura);
      } catch (error) {
        console.error('Error cargando dropdowns:', error);
      } finally {
        loadingRef.current = false;
      }
    };

    loadDropdowns();
  }, [isFilterModalOpen, comprobantesLoaded, estadosLoaded, setComprobantes, setEstadosFactura, setLoadingComprobantes, setLoadingEstados]);

  useEffect(() => {
    if (!isFilterModalOpen || !username || loadingSectoresRef.current) {
      return;
    }

    if (!needsReloadSectores(username, centroCosto)) {
      return;
    }

    const loadSectores = async () => {
      loadingSectoresRef.current = true;
      setLoadingSectores(true);

      try {
        const sectoresData = await fetchSectoresDropdown(username, centroCosto);
        setSectores(sectoresData, username, centroCosto);
      } catch (error) {
        console.error('Error cargando sectores:', error);
      } finally {
        setLoadingSectores(false);
        loadingSectoresRef.current = false;
      }
    };

    loadSectores();
  }, [isFilterModalOpen, username, centroCosto, needsReloadSectores, setSectores, setLoadingSectores]);

  useEffect(() => {
    if (isFilterModalOpen) {
      setTipoFacturaSelected(null);
      setEstadoFacturaSelected(null);
      setSectorSelected(null);
    }
  }, [isFilterModalOpen]);


  const handleAccept = (onFiltersApply) => {
    // Construir objeto de filtros para enviar al backend
    const filters = {};

    // Mapear los filtros según los parámetros esperados por el backend
    if (razonSocial) {
      filters.razon_social = razonSocial;
    }

    if (tipoFacturaSelected) {
      filters.tipo_factura = tipoFacturaSelected;
    }

    if (estadoFacturaSelected) {
      filters.estado = estadoFacturaSelected;
    }

    if (fechaDesde) {
      filters.fecha_desde = fechaDesde;
    }

    if (fechaHasta) {
      filters.fecha_hasta = fechaHasta;
    }

    if (importeDesde) {
      filters.importe_desde = formatImporteToAmericano(importeDesde);
    }

    if (importeHasta) {
      filters.importe_hasta = formatImporteToAmericano(importeHasta);
    }

    if (sectorSelected) {
      // Manejar selección múltiple
      if (Array.isArray(sectorSelected)) {
        const ids = sectorSelected.map(desc => {
          const found = sectores.find((s) => s.DESCRIPCION === desc);
          return found?.ID;
        }).filter(id => id != null);
        
        if (ids.length > 0) {
          filters.sector_destino = ids;
        }
      } else {
        const found = sectores.find((s) => s.DESCRIPCION === sectorSelected);
        if (found?.ID) {
          filters.sector_destino = found.ID;
        }
      }
    }

    // Llamar al callback con los filtros aplicados
    if (onFiltersApply) {
      onFiltersApply(filters);
    }

    setIsFilterModalOpen(false);
  };

  /**
   * Maneja la limpieza de filtros
   */
  const handleClear = () => {
    resetFilters();
    setTipoFacturaSelected(null);
    setEstadoFacturaSelected(null);
    setImporteDesde("");
    setImporteHasta("");
    setRazonSocial("");
    setFechaDesde("");
    setFechaHasta("");
    setSectorSelected(null);
    // Forzar re-render de los dropdowns
    setResetKey(prev => prev + 1);
  };

  const handleTipoFacturaChange = (value) => {
    if (value == null || value === "") {
      setTipoFacturaSelected(null);
      setTipoFactura({ id: null, desc: null });
      return;
    }
    const desc = value;
    const found = comprobantes.find((c) => c.DESCRIPCION === desc);
    const id = found?.ID ?? null;
    setTipoFacturaSelected(desc);
    setTipoFactura({ id, desc });
  };


  const handleEstadoFacturaChange = (value) => {
    if (value == null || value === '') {
      setEstadoFacturaSelected(null);
      setEstadoFactura({ id: null, desc: null });
      return;
    }
    const desc = value;
    const found = estadosFactura.find((e) => e.DESCRIPCION === desc);
    const id = found?.ID ?? null;
    setEstadoFacturaSelected(desc);
    setEstadoFactura({ id, desc });
  };

  const handleSectorChange = (value) => {
    if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
      setSectorSelected(null);
      setSector({ id: null, desc: null });
      return;
    }
    
    // Manejar selección múltiple
    if (Array.isArray(value)) {
      const ids = value.map(desc => {
        const found = sectores.find((s) => s.DESCRIPCION === desc);
        return found?.ID;
      }).filter(id => id != null);
      
      const descs = value;
      setSectorSelected(descs);
      setSector({ id: ids, desc: descs });
    } else {
      const found = sectores.find((s) => s.DESCRIPCION === value);
      const id = found?.ID ?? null;
      setSectorSelected(value);
      setSector({ id, desc: value });
    }
  };

  const handleRazonSocialChange = (e) => {
    const v = typeof e === 'string' ? e : (e?.target?.value ?? '');
    setRazonSocial(v);
    setRazonSocialStore(v);
  };

  const handleImporteDesdeChange = (val) => {
    setImporteDesde(val);
    setImporteDesdeStore(val);
  };

  const handleImporteHastaChange = (val) => {
    setImporteHasta(val);
    setImporteHastaStore(val);
  };

  const handleFechaDesdeChange = (val) => {
    setFechaDesde(val);
    setFechaDesdeStore(val);
  };

  const handleFechaHastaChange = (val) => {
    setFechaHasta(val);
    setFechaHastaStore(val);
  };

  return {
    // Estados
    isFilterModalOpen,
    setIsFilterModalOpen,
    resetKey,
    comprobantes,
    estadosFactura,
    sectores,

    // Estados del formulario
    tipoFacturaSelected,
    estadoFacturaSelected,
    importeDesde,
    importeHasta,
    razonSocial,
    fechaDesde,
    fechaHasta,
    sectorSelected,

    // Handlers
    handleAccept,
    handleClear,
    handleTipoFacturaChange,
    handleEstadoFacturaChange,
    handleRazonSocialChange,
    handleImporteDesdeChange,
    handleImporteHastaChange,
    handleFechaDesdeChange,
    handleFechaHastaChange,
    handleSectorChange,
  };
};
